import { Button, ButtonGroup, Icon } from "@blueprintjs/core";
import * as monaco from "monaco-editor-core";
import { IDisposable } from "monaco-editor-core";

import { Vec2 } from "../../Data/Geometry/Vec2";
import { Panic } from "../../Errors/Panic";
import styles from "./DiagnosticsPopup.module.scss";
import { createPopup, Popup } from "./Popup";

export class DiagnosticsPopup<DiagnosticId> implements IDisposable {
  static getSize(): { width: number; height: number } {
    return {
      width: 540,
      height: 108
    };
  }

  /**
   * @param diagnostics A value of `null` means loading
   */
  constructor(
    position: Vec2,
    private diagnostics: DiagnosticsPopup.Diagnostic<DiagnosticId>[] | null,
    private listener: DiagnosticsPopup.Events
  ) {
    this.popup = createPopup(
      {
        animationTransform: { x: 0, y: 44 },
        position: position,
        size: DiagnosticsPopup.getSize(),
        titleBarClassName: styles.titlebar,
        renderTitleBar: () => (
          <TitleBar
            currentDiagnosticNum={this.currentDiagnosticIndex + 1}
            numDiagnostics={this.diagnostics === null ? 0 : this.diagnostics.length}
            onLeftButtonClick={this.listener.onLeftButtonClick}
            onRightButtonClick={this.listener.onRightButtonClick}
          />
        )
      },
      {
        onExpand: this.listener.onExpand
      }
    );

    this.editor = createMonacoEditor(this.popup.content);
    this.editor.editorInstance.focus();
    if (diagnostics === null) {
      // TODO Make this a lot nicer
      this.editor.model.setValue("Loading...\n");
    } else {
      if (diagnostics.length > 0) {
        this.editor.model.setValue(diagnostics[0].message + "\n");
      } else {
        this.editor.model.setValue("No Diagnostics\n");
      }
    }
  }

  getCurrentDiagnosticIndex(): number {
    return this.currentDiagnosticIndex;
  }

  setCurrentDiagnosticIndex(currentDiagnosticIndex: number): void {
    this.currentDiagnosticIndex = currentDiagnosticIndex;
    this.popup.renderTitleBar();
    if (this.diagnostics !== null) {
      this.editor.model.setValue(this.diagnostics[currentDiagnosticIndex].message + "\n");
    }
  }

  dispose(): void {
    this.editor.model.dispose();
    this.editor.editorInstance.dispose();
    this.popup.dispose();
  }

  private popup: Popup;
  private editor: Editor;
  private currentDiagnosticIndex: number = 0;
}

export declare namespace DiagnosticsPopup {
  export interface Events {
    onExpand: () => void;
    onLeftButtonClick: () => void;
    onRightButtonClick: () => void;
  }

  export interface Diagnostic<DiagnosticId> {
    id: DiagnosticId;
    message: string;
  }
}

interface Editor {
  editorInstance: monaco.editor.IStandaloneCodeEditor;
  model: monaco.editor.ITextModel;
}

function createMonacoEditor(parent: HTMLElement): Editor {
  // "Is there a way to disable IntelliSense?" <https://github.com/microsoft/monaco-editor/issues/1681>
  const editorInstance = monaco.editor.create(parent, {
    readOnly: true,
    lineNumbers: "on",
    lineNumbersMinChars: 2,
    padding: {
      top: 3,
      bottom: 3
    },
    renderIndentGuides: false,
    codeLens: false,
    lightbulb: {
      enabled: false
    },
    glyphMargin: false,
    // lineDecorationsWidth: 0,
    matchBrackets: "never",
    selectionHighlight: false,
    tabSize: 2,
    automaticLayout: true,
    scrollbar: {
      verticalHasArrows: true,
      useShadows: false,
      arrowSize: 15,
      horizontalScrollbarSize: 15,
      verticalScrollbarSize: 15
    },
    detectIndentation: false,
    autoClosingBrackets: "never",
    quickSuggestions: false,
    snippetSuggestions: "none",
    occurrencesHighlight: false,
    contextmenu: false,
    scrollBeyondLastLine: false,
    autoIndent: "keep",
    suggest: {
      showStatusBar: false,
      showSnippets: false
    },
    dragAndDrop: false,
    suggestOnTriggerCharacters: false,
    minimap: {
      enabled: false
    },
    folding: false,
    hideCursorInOverviewRuler: true,
    renderLineHighlight: "none"
  });

  const model = editorInstance.getModel();
  if (model === null) {
    throw new Panic("IStandaloneCodeEditor missing default model", {});
  }

  return {
    editorInstance: editorInstance,
    model: model
  };
}

interface TitleBarProps {
  currentDiagnosticNum: number;
  numDiagnostics: number;
  onLeftButtonClick: () => void;
  onRightButtonClick: () => void;
}

function TitleBar(props: TitleBarProps): JSX.Element {
  return (
    <>
      <Icon icon="highlight" className={styles.titlebar_icon} /> Diagnostics
      {props.numDiagnostics > 1 ? (
        <>
          {" "}
          ({props.currentDiagnosticNum}/{props.numDiagnostics}){" "}
          <ButtonGroup className={styles.titlebar_button_group}>
            <Button
              className={styles.toolbar_button}
              rightIcon={<Icon icon="caret-left" size={14} />}
              onClick={props.onLeftButtonClick}
            />
            <Button
              className={styles.toolbar_button}
              rightIcon={<Icon icon="caret-right" size={14} />}
              onClick={props.onRightButtonClick}
            />
          </ButtonGroup>
        </>
      ) : (
        ""
      )}
    </>
  );
}
